import { GroupMember, GroupSummary, PlanType } from '../../../services/patient_plan/types/types';
import planDuoIcon from './icons/plan-duo.svg';
import planFamilyIcon from './icons/plan-family.svg';
import planIndividualIcon from './icons/plan-individual.svg';

export const iconByType: Record<PlanType, string> = {
  INDIVIDUAL: planIndividualIcon,
  DUO: planDuoIcon,
  FAMILY: planFamilyIcon,
};

export function planTypeLabel(t: PlanType): 'Individual' | 'Duo' | 'Family' {
  switch (t) {
    case 'INDIVIDUAL':
      return 'Individual';
    case 'DUO':
      return 'Duo';
    case 'FAMILY':
      return 'Family';
    default:
      return 'Individual';
  }
}

export function initialsFrom(nameOrEmail: string): string {
  const tokens = nameOrEmail.trim().split(/\s+/).filter(Boolean);
  if (tokens.length >= 2) return (tokens[0][0] + tokens[1][0]).toUpperCase();
  if (tokens.length === 1) return tokens[0].slice(0, 2).toUpperCase();
  return '??';
}

export function formatDate(iso?: string): string {
  if (!iso) return '';
  // A date-only value parses as UTC midnight, which renders as the day before
  // anywhere west of Greenwich — an expiry of 12/31 would read 12/30 for every
  // US patient. Read the parts directly rather than going through a Date.
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (dateOnly) {
    const [, yyyy, mm, dd] = dateOnly;
    return `${Number(mm)}/${Number(dd)}/${yyyy.slice(-2)}`;
  }
  const d = new Date(iso);
  const mm = d.getMonth() + 1;
  const dd = d.getDate();
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

export const roleText = (m?: GroupMember | null): string => {
  if (!m) return '';
  if (m.role === 'OWNER') return 'Plan manager';
  return m.role === 'DEPENDENT' ? 'Plan member (dependent)' : 'Plan member';
};

export const clamp = (n: number, min: number, max: number): number => Math.max(min, Math.min(max, n));

export const initials = (full_name?: string | null, email?: string | null): string => {
  const nameParts = (full_name || '').trim().split(' ');
  const f = nameParts[0] || '';
  const l = nameParts[1] || '';
  if (f && l) return (f[0] + l[0]).toUpperCase();
  const token = (f || l || email || '??').trim();
  return token.slice(0, 2).toUpperCase();
};

export const fullName = (p: { full_name?: string | null; email?: string | null; id?: number }): string =>
  p.full_name || p.email || (p.id ? `User #${p.id}` : 'User');

export const getPlanName = (group: GroupSummary): string => {
  if (!group?.name) return 'No Plan';

  if (group.name.includes('Revenue')) return 'Discount Plan';
  if (group.name.includes('Discount')) return 'Discount+ Plan';
  if (group.name.includes('Essential')) return 'Essential Plan';
  if (group.name.includes('Care+')) return 'Care+ Plan';
  if (group.name.includes('Unlimited')) return 'Unlimited Plan';

  return group.name; // fallback to original
};

export const getPlanDescription = (group: GroupSummary): string => {
  if (!group) return 'You don’t have an active plan yet.';

  const perUserSuffix = group.type && group.type !== 'INDIVIDUAL' ? ' per user' : '';

  if (group.percent_off) {
    return `Your plan includes a ${group.percent_off}% discount on every visit${perUserSuffix}`;
  }

  if (group.unlimited) {
    return `Your plan includes unlimited free services per calendar year${perUserSuffix}`;
  }

  if (group.free_cases_per_year) {
    return `Your plan includes ${group.free_cases_per_year} free service${
      group.free_cases_per_year > 1 ? 's' : ''
    } per calendar year${perUserSuffix}`;
  }

  return 'You don’t have an active plan yet.';
};

export const safeCount = (n: unknown): number => {
  const v = Number(n);
  if (!Number.isFinite(v) || Number.isNaN(v) || v <= 0) return 0;
  return Math.floor(v);
};
